import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

type TcoHeaderLegend = {
  label: string;
  color: string;
};

type TcoHeaderMarkerLegend = {
  shortLabel: string;
  label: string;
};

@Component({
  selector: 'app-tco-header',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './tco-header.component.html',
  styleUrls: ['./tco-header.component.scss']
})
export class TcoHeaderComponent implements OnDestroy {
  private destroy$ = new Subject<void>();
  private _data: any;
  private apiSubtitle = '';
  private activeRegion = '';
  private responseSeriesLabels: string[] = [];

  set data(value: any) {
    this._data = value;
    this.apiSubtitle = '';
    this.responseSeriesLabels = [];
    this.onDataAssigned();
  }

  get data(): any {
    return this._data;
  }

  constructor(
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService
  ) {
    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        this.activeRegion = this.resolveRegion(filters);

        if (!this.data?.apiUrl || Object.keys(filters || {}).length === 0) {
          return;
        }

        this.loadHeaderData(filters);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get title(): string {
    return `${this.data?.title ?? 'Total Cost of Ownership, $/h'}`;
  }

  get subtitle(): string {
    const subtitle = `${this.apiSubtitle || this.buildSubtitleFromState() || this.data?.subtitle || ''}`.trim();
    return subtitle;
  }

  get legends(): TcoHeaderLegend[] {
    const fallback: TcoHeaderLegend[] = [
      { label: 'CMI Actual', color: '#df2a21' },
      { label: 'CMI Baseline', color: '#1f2f4d' },
      { label: 'CAT Actual', color: '#f2bc00' },
      { label: 'MTU Actual', color: '#2a4699' }
    ];

    const configuredLegends = Array.isArray(this.data?.legends) && this.data.legends.length > 0
      ? this.data.legends
      : fallback;
    const region = this.resolveRegion();
    const regionOverrideLegends = this.getRegionOverrideLegends(region);

    if (region === 'CHINA') {
      const chinaLegends = this.buildChinaLegendsFromResponse(configuredLegends, regionOverrideLegends);
      if (chinaLegends.length > 0) {
        return chinaLegends;
      }

      // Before response labels are available, keep configured legend order.
      return configuredLegends
        .filter((item: any) => item && `${item?.label ?? ''}`.trim().length > 0)
        .map((item: any) => ({
          label: `${item.label}`.trim(),
          color: `${item.color ?? '#1f2f4d'}`
        }));
    }

    const legendsSource = Array.isArray(regionOverrideLegends) && regionOverrideLegends.length > 0
      ? regionOverrideLegends
      : configuredLegends;

    if (!Array.isArray(legendsSource) || legendsSource.length === 0) {
      return fallback;
    }

    return legendsSource
      .filter((item: any) => item && `${item?.label ?? ''}`.trim().length > 0)
      .map((item: any) => ({
        label: this.resolveLegendLabelFromResponse(`${item.label}`),
        color: `${item.color ?? '#1f2f4d'}`
      }));
  }

  get markerLegends(): TcoHeaderMarkerLegend[] {
    const fallback: TcoHeaderMarkerLegend[] = [
      { shortLabel: 'R', label: 'Rebuild Price' },
      { shortLabel: 'M', label: 'Maintenance-Midlife' }
    ];

    if (!Array.isArray(this.data?.markerLegends) || this.data.markerLegends.length === 0) {
      return fallback;
    }

    return this.data.markerLegends
      .filter((item: any) =>
        item &&
        `${item?.shortLabel ?? ''}`.trim().length > 0 &&
        `${item?.label ?? ''}`.trim().length > 0
      )
      .map((item: any) => ({
        shortLabel: `${item.shortLabel}`.trim(),
        label: `${item.label}`.trim()
      }));
  }

  get shouldShowMarkerLegends(): boolean {
    const raw = this.data?.showMarkerLegends;
    if (raw === undefined || raw === null) {
      return true;
    }

    if (typeof raw === 'string') {
      return raw.toLowerCase() !== 'false';
    }

    return Boolean(raw);
  }

  private onDataAssigned(): void {
    this.activeRegion = this.resolveRegion();

    if (!this.data?.apiUrl) {
      return;
    }

    this.loadHeaderData();
  }

  private loadHeaderData(filters?: any): void {
    this.configService.getConfig(
      this.data.apiUrl,
      this.data,
      { ...filters, useQueryParams: this.data?.useQueryParams || false },
      this.data.method
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => this.bindSubtitleFromResponse(res),
      error: () => {
        this.apiSubtitle = '';
        this.responseSeriesLabels = [];
      }
    });
  }

  private bindSubtitleFromResponse(response: any): void {
    const responsePath = `${this.data?.responsePath ?? 'responseList.0.ui-data.0.data.0'}`.trim();
    const source = responsePath ? this.getByPath(response, responsePath) : response;
    this.responseSeriesLabels = this.extractSeriesLabels(source);
    const navState = (window?.history?.state || {}) as any;
    const mine = `${navState?.selectedMineName ?? navState?.selectedMine?.name ?? source?.mine ?? source?.requestData?.mine ?? ''}`.trim();
    const engineModel = `${navState?.selectedEngineModelKey ?? navState?.engine_model_key ?? navState?.selectedEngine ?? navState?.engine_model ?? source?.engine_model_key ?? source?.engineModelKey ?? source?.engine_model ?? ''}`.trim();

    if (!mine && !engineModel) {
      this.apiSubtitle = '';
      return;
    }

    this.apiSubtitle = [mine, engineModel].filter((part) => part.length > 0).join(' - ');
  }

  private buildSubtitleFromState(): string {
    const navState = (window?.history?.state || {}) as any;
    const mine = `${navState?.selectedMineName ?? navState?.selectedMine?.name ?? ''}`.trim();
    const engineModel = `${navState?.selectedEngineModelKey ?? navState?.engine_model_key ?? navState?.selectedEngine ?? navState?.engine_model ?? ''}`.trim();
    return [mine, engineModel].filter((part) => part.length > 0).join(' - ');
  }

  private getByPath(source: any, path: string): any {
    return path.split('.').reduce((acc: any, key: string) => {
      if (acc === null || acc === undefined) {
        return undefined;
      }

      if (/^\d+$/.test(key)) {
        return acc[Number(key)];
      }

      return acc[key];
    }, source);
  }

  private getRegionOverrideLegends(region: string): any[] | undefined {
    if (!region) {
      return undefined;
    }

    const overrides = this.data?.legendOverrides;
    if (!overrides || typeof overrides !== 'object') {
      return undefined;
    }

    return overrides[region];
  }

  private resolveRegion(filters?: any): string {
    const navState = (window?.history?.state || {}) as any;
    const requestParams = filters?.requestParams;
    const queryParams = filters?.queryParams;
    const fromFilters = this.normalizeRegion(
      requestParams?.region ??
      queryParams?.region ??
      filters?.region ??
      filters?.Region ??
      filters?.selectedRegion ??
      filters?.selected_region
    );
    if (fromFilters) {
      return fromFilters;
    }

    const fromState = this.normalizeRegion(
      navState?.selectedRegion ??
      navState?.region ??
      navState?.selected_region ??
      navState?.selectedMine?.region ??
      navState?.selectedMine?.__regionRaw ??
      navState?.selectedMine?.__regionNorm
    );
    if (fromState) {
      return fromState;
    }

    return this.normalizeRegion(this.activeRegion);
  }

  private normalizeRegion(rawRegion: any): string {
    let value = rawRegion;

    while (Array.isArray(value) && value.length > 0) {
      value = value[0];
    }

    if (value && typeof value === 'object') {
      value = value.value ?? value.key ?? value.id ?? value.label ?? '';
    }

    return `${value ?? ''}`.trim().toUpperCase();
  }

  private resolveLegendLabelFromResponse(label: string): string {
    const normalized = this.normalizeLegendToken(label);
    if (!normalized || !Array.isArray(this.responseSeriesLabels) || this.responseSeriesLabels.length === 0) {
      return label;
    }

    const directMatch = this.responseSeriesLabels.find((seriesLabel) =>
      this.normalizeLegendToken(seriesLabel) === normalized
    );
    if (directMatch) {
      return directMatch;
    }

    const configuredLegends = Array.isArray(this.data?.legends) ? this.data.legends : [];
    const configuredIndex = configuredLegends.findIndex((item: any) =>
      this.normalizeLegendToken(item?.label ?? '') === normalized
    );
    if (configuredIndex >= 0 && this.responseSeriesLabels[configuredIndex]) {
      return this.responseSeriesLabels[configuredIndex];
    }

    return label;
  }

  private extractSeriesLabels(source: any): string[] {
    const candidateGroups = [
      source?.lcc,
      source?.fuel,
      source?.def,
      source?.TotalTco,
      source?.total_Lto,
      source?.lcc_variance_pct,
      source?.fuel_variance_pct,
      source?.def_variance_pct,
      source?.tco_dollar_hr_variance_pct,
      source?.total_tco_variance_pct
    ];

    const labels: string[] = [];
    const seen = new Set<string>();
    const labelsWithValue = new Set<string>();

    for (const group of candidateGroups) {
      if (!group || typeof group !== 'object') {
        continue;
      }

      const entries = Object.entries(group);
      for (const [key, rawValue] of entries) {
        const label = `${key ?? ''}`.trim();
        if (!label) {
          continue;
        }

        const token = this.normalizeLegendToken(label);
        if (!seen.has(token)) {
          seen.add(token);
          labels.push(label);
        }

        if (this.hasMeaningfulLegendValue(rawValue)) {
          labelsWithValue.add(token);
        }
      }
    }

    return labels.filter((label) => labelsWithValue.has(this.normalizeLegendToken(label)));
  }

  private hasMeaningfulLegendValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'number') {
      return Number.isFinite(value);
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (!normalized) {
        return false;
      }

      return normalized !== 'null' && normalized !== 'undefined' && normalized !== 'nan';
    }

    if (Array.isArray(value)) {
      return value.some((entry) => this.hasMeaningfulLegendValue(entry));
    }

    if (typeof value === 'object') {
      return Object.values(value).some((entry) => this.hasMeaningfulLegendValue(entry));
    }

    return true;
  }

  private buildChinaLegendsFromResponse(
    configuredLegends: any[],
    regionOverrideLegends?: any[]
  ): TcoHeaderLegend[] {
    if (!Array.isArray(this.responseSeriesLabels) || this.responseSeriesLabels.length === 0) {
      return [];
    }

    const mergedSources = [
      ...(Array.isArray(configuredLegends) ? configuredLegends : []),
      ...(Array.isArray(regionOverrideLegends) ? regionOverrideLegends : [])
    ];

    const legends: TcoHeaderLegend[] = [];
    const seen = new Set<string>();
    this.responseSeriesLabels.forEach((label: string) => {
      const cleanedLabel = `${label ?? ''}`.trim();
      if (!cleanedLabel) {
        return;
      }

      const token = this.normalizeLegendToken(cleanedLabel);
      if (seen.has(token)) {
        return;
      }
      seen.add(token);

      legends.push({
        label: cleanedLabel,
        color: this.resolveLegendColorForLabel(cleanedLabel, mergedSources) || '#1f2f4d'
      });
    });

    return legends;
  }

  private normalizeLegendToken(label: any): string {
    return `${label ?? ''}`
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9 ]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private splitLegendToken(token: string): { vendor: string; qualifier: string } {
    const normalized = this.normalizeLegendToken(token);
    if (!normalized) {
      return { vendor: '', qualifier: '' };
    }

    const parts = normalized.split(' ').filter((part) => part.length > 0);
    if (parts.length === 1) {
      return { vendor: parts[0], qualifier: '' };
    }

    return {
      vendor: parts.slice(0, parts.length - 1).join(' '),
      qualifier: parts[parts.length - 1]
    };
  }

  private computeEditDistance(left: string, right: string): number {
    if (left === right) {
      return 0;
    }

    const a = left || '';
    const b = right || '';
    const dp: number[][] = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));

    for (let i = 0; i <= a.length; i++) {
      dp[i][0] = i;
    }
    for (let j = 0; j <= b.length; j++) {
      dp[0][j] = j;
    }

    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
      }
    }

    return dp[a.length][b.length];
  }

  private resolveLegendColorForLabel(label: string, entries: any[]): string | null {
    if (!label || !Array.isArray(entries) || entries.length === 0) {
      return null;
    }

    const targetToken = this.normalizeLegendToken(label);
    if (!targetToken) {
      return null;
    }

    for (const entry of entries) {
      if (!entry || !entry.label || !entry.color) {
        continue;
      }

      if (this.normalizeLegendToken(entry.label) === targetToken) {
        return `${entry.color}`;
      }
    }

    const targetParts = this.splitLegendToken(targetToken);
    if (!targetParts.vendor || !targetParts.qualifier) {
      return null;
    }

    let bestColor: string | null = null;
    let bestDistance = Number.MAX_SAFE_INTEGER;
    for (const entry of entries) {
      if (!entry || !entry.label || !entry.color) {
        continue;
      }

      const candidateToken = this.normalizeLegendToken(entry.label);
      const candidateParts = this.splitLegendToken(candidateToken);
      if (!candidateParts.vendor || candidateParts.qualifier !== targetParts.qualifier) {
        continue;
      }

      const distance = this.computeEditDistance(targetParts.vendor, candidateParts.vendor);
      if (distance <= 2 && distance < bestDistance) {
        bestDistance = distance;
        bestColor = `${entry.color}`;
      }
    }

    return bestColor;
  }
}
